import { useQuery } from '@tanstack/react-query';
import { Compass } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { BrandIcon, BrandLogo, useBrandLogo } from '../components/brand.js';
import { ColorModeButton, ColorModeSwitch } from '../components/color-mode.js';
import { Card, cn, EmptyState, ErrorBanner, Spinner } from '../components/ui.js';
import { formatDate } from '../lib/format.js';
import { ReadOnlyView } from '../lib/read-only.js';
import { useBranding } from '../lib/theme.js';
import { errorMessage, trpcClient } from '../lib/trpc.js';
import { SamplePill, ViewBody, type FigureLoader } from './owner-dashboard.js';
import { markTourSeen, Tour, tourSeen } from './viewer-tour.js';

/**
 * `/view#<link>`: one organization's owner dashboard opened by a read-only view link, with no
 * sign-in. The link's token is after the `#`, so it never reaches a server log; it is sent
 * to the API in a POST body. Nothing here links into the app.
 */

/** While the dashboard is still being made, look again this often. */
const BUILDING_POLL_MS = 10_000;

function tokenFromHash(): string {
  const hash = window.location.hash.replace(/^#/, '');
  try {
    return decodeURIComponent(hash).trim();
  } catch {
    // Mangled on the way, say by an email program: the API says it is not valid.
    return hash.trim();
  }
}

export function ViewerScreen() {
  const product = useBranding()?.product_name ?? '';
  const logo = useBrandLogo();
  const [token, setToken] = useState(tokenFromHash);
  useEffect(() => {
    const changed = () => setToken(tokenFromHash());
    window.addEventListener('hashchange', changed);
    return () => window.removeEventListener('hashchange', changed);
  }, []);
  const opened = useQuery({
    queryKey: ['viewer', 'open', token],
    queryFn: () => trpcClient.viewer.open.mutate({ token }),
    enabled: token !== '',
    refetchInterval: (query) => (query.state.data?.state === 'building' ? BUILDING_POLL_MS : false),
  });
  const data = opened.data;
  const ready = data?.state === 'ready' ? data : null;
  const book = ready?.invite?.links[0];

  return (
    <ReadOnlyView.Provider value={true}>
      <div className="min-h-full bg-wash">
        <header className="border-b border-line bg-surface">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-3">
            {logo ? (
              <BrandLogo alt={product} className="h-6 w-auto sm:h-8" />
            ) : (
              <span className="flex items-center gap-3">
                <BrandIcon name={product} className="size-8" tile="rounded-lg text-sm" />
                <span className="text-sm font-semibold text-ink">{product}</span>
              </span>
            )}
            <div className="flex items-center gap-3">
              <span className="hidden sm:contents">
                <ColorModeSwitch />
              </span>
              <span className="contents sm:hidden">
                <ColorModeButton />
              </span>
              {book && (
                <a
                  href={book.href}
                  {...outLink(book.href)}
                  data-tour="book"
                  className="rounded-lg bg-brand px-3.5 py-2 text-sm font-semibold whitespace-nowrap text-white hover:bg-brand-deep"
                >
                  {book.label}
                </a>
              )}
            </div>
          </div>
        </header>
        <main className="mx-auto max-w-7xl px-4 py-6 lg:py-8">
          {token === '' ? (
            <Notice title="This link is incomplete">
              Open the whole link you were sent, including everything after the #.
            </Notice>
          ) : opened.isLoading ? (
            <Spinner />
          ) : opened.error || !data ? (
            <ErrorBanner title="Could not open this dashboard">
              {errorMessage(opened.error)}
            </ErrorBanner>
          ) : data.state === 'invalid' ? (
            <Notice title="This link is not valid">
              Check that you opened the whole link you were sent.
            </Notice>
          ) : data.state === 'ended' ? (
            <Notice title={data.message}>
              {data.links.length > 0 && (
                <span className="mt-3 flex flex-wrap justify-center gap-3">
                  {data.links.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="font-medium text-brand-strong hover:underline"
                    >
                      {link.label}
                    </a>
                  ))}
                </span>
              )}
            </Notice>
          ) : data.state === 'building' ? (
            <Notice title="Your dashboard is still being built">
              This page will show it as soon as it is ready.
            </Notice>
          ) : (
            <Dashboard
              token={token}
              company={data.company}
              zone={data.timezone}
              expiresAt={data.expires_at}
              views={data.views}
              invite={data.invite}
              tour={data.tour}
            />
          )}
        </main>
      </div>
    </ReadOnlyView.Provider>
  );
}

function Notice({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <Card>
      <EmptyState title={title}>{children}</EmptyState>
    </Card>
  );
}

type Invite = { title: string; text: string | null; links: { label: string; href: string }[] };

/** A link out of the page: a web address opens in a new tab, an email address as it is. */
function outLink(href: string) {
  return href.startsWith('https:') ? { target: '_blank', rel: 'noopener noreferrer' } : {};
}

/** What the deploy offers beside the dashboard, such as booking a call, its first link the button. */
function InviteCard({ invite }: { invite: Invite }) {
  const [first, ...rest] = invite.links;
  return (
    <section
      data-testid="viewer-invite"
      aria-labelledby="viewer-invite"
      className="mt-6 rounded-2xl bg-night p-6 text-white dark:border dark:border-line-2"
    >
      <h2 id="viewer-invite" className="text-lg font-semibold text-balance">
        {invite.title}
      </h2>
      {invite.text && <p className="mt-1 text-sm text-white/80">{invite.text}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-4">
        {first && (
          <a
            href={first.href}
            {...outLink(first.href)}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-deep"
          >
            {first.label}
          </a>
        )}
        {rest.map((link) => (
          <a
            key={link.href}
            href={link.href}
            {...outLink(link.href)}
            className="text-sm font-medium text-white underline"
          >
            {link.label}
          </a>
        ))}
      </div>
    </section>
  );
}

function Dashboard({
  token,
  company,
  zone,
  expiresAt,
  views,
  invite,
  tour,
}: {
  token: string;
  company: string;
  zone: string;
  expiresAt: string;
  views: { key: string; label: string; description: string | null }[];
  invite: Invite | null;
  tour: { welcome: string } | null;
}) {
  const first = views[0]?.key ?? '';
  const [key, setKey] = useState(first);
  const [touring, setTouring] = useState(false);
  const view = useQuery({
    queryKey: ['viewer', 'view', token, key],
    queryFn: () => trpcClient.viewer.view.mutate({ token, key }),
    enabled: key !== '',
    staleTime: 60_000,
  });
  const figures = useMemo<FigureLoader>(
    () => ({
      key: (view, figure) => ['viewer', 'figure', token, view, figure],
      load: (view, figure) => trpcClient.viewer.figure.mutate({ token, view, key: figure }),
    }),
    [token],
  );
  const summary = views.find((v) => v.key === key);
  const until = formatDate(expiresAt.slice(0, 10));
  const onFirst = key === first && view.data?.key === first;
  const endTour = () => {
    markTourSeen();
    setTouring(false);
  };
  // The first visit to the first view opens the tour by itself, once per browser. Its stops are
  // on the first view, so moving to another view ends it.
  useEffect(() => {
    if (tour && onFirst && !tourSeen()) setTouring(true);
  }, [tour, onFirst]);
  useEffect(() => {
    if (!touring || key === first) return;
    markTourSeen();
    setTouring(false);
  }, [touring, key, first]);
  const takeTour = () => {
    setKey(first);
    setTouring(true);
  };
  const tabs = (className: string, item: string, active: string) => (
    <nav aria-label="Dashboard views" data-tour="views" className={className}>
      {views.map((v) => (
        <button
          key={v.key}
          type="button"
          onClick={() => setKey(v.key)}
          aria-current={v.key === key ? 'page' : undefined}
          className={cn(item, v.key === key && active)}
        >
          {v.label}
        </button>
      ))}
    </nav>
  );
  const tourButton = tour && (
    <button
      type="button"
      onClick={takeTour}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-strong hover:underline"
    >
      <Compass className="size-4" aria-hidden />
      Take the tour
    </button>
  );

  return (
    <>
      {/* The sample screen on databink.com, made real: views down the side, the day on the right. */}
      <div className="flex overflow-hidden rounded-2xl border border-line-2 bg-surface shadow-[0_-2px_60px_rgb(0_128_192/0.12)]">
        <aside className="hidden w-60 shrink-0 flex-col border-r border-line bg-panel px-4 py-5 lg:flex">
          <p className="px-2.5 text-[15px] font-bold text-ink">{company}</p>
          {tabs(
            'mt-3 flex flex-col gap-0.5',
            'rounded-lg px-2.5 py-2 text-left text-sm text-ink-2 hover:bg-surface',
            'bg-brand-soft font-semibold text-brand-strong hover:bg-brand-soft',
          )}
          <div className="mt-auto space-y-2 px-2.5 pt-8">
            {tourButton}
            <p className="text-xs text-ink-3">This link works until {until}.</p>
          </div>
        </aside>
        <div className="min-w-0 flex-1 px-4 py-5 sm:px-6 lg:py-6">
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
            <div className="min-w-0">
              <h1 className="text-xl font-bold tracking-tight text-ink sm:text-2xl">
                {/* The company is in the sidebar on a wide screen, so only a screen reader hears it. */}
                <span className="lg:sr-only">
                  {company}
                  <span className="text-ink-3"> · </span>
                </span>
                {summary?.label ?? ''}
              </h1>
              {summary?.description && (
                <p className="mt-1 text-sm text-ink-3">{summary.description}</p>
              )}
            </div>
            {view.data?.sample && (
              <SamplePill title="Every number here is invented for a demonstration, dated to today." />
            )}
          </div>
          {tabs(
            '-mx-4 mt-4 flex gap-1.5 overflow-x-auto px-4 pb-1 lg:hidden',
            'shrink-0 rounded-full border border-line px-3 py-1.5 text-sm whitespace-nowrap text-ink-2',
            'border-brand-soft bg-brand-soft font-semibold text-brand-strong',
          )}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 lg:hidden">
            <p className="text-xs text-ink-3">This link works until {until}.</p>
            {tourButton}
          </div>
          <div className="mt-5">
            {views.length === 0 ? (
              <Notice title="There is nothing to show yet" />
            ) : view.isLoading ? (
              <Spinner />
            ) : view.error || !view.data ? (
              <ErrorBanner title="Could not show this view">{errorMessage(view.error)}</ErrorBanner>
            ) : (
              <ViewBody
                view={view.data}
                zone={zone}
                company={company}
                sampleNote={false}
                figures={figures}
              />
            )}
          </div>
        </div>
      </div>
      {invite && <InviteCard invite={invite} />}
      {tour && touring && onFirst && view.data && (
        <Tour
          company={company}
          welcome={tour.welcome}
          view={view.data}
          views={views}
          invite={invite}
          onClose={endTour}
        />
      )}
    </>
  );
}
